/*
 * Copyright (c) 2023. Collabro Limited
 */

import React, { memo, useContext, useEffect, useState } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { Slider, TextField, TextFieldProps } from "@mui/material";
import { DateTime } from "luxon";
import { AuthenticatedContext } from "../Authenticated/Authenticated";

interface DateRangeComponent {
  endDate: DateTime;
  setEndDate: any;
}

const DateRange = ({ endDate, setEndDate }: DateRangeComponent) => {
  const [lookaheadValue, setLookaheadValue] = useState<number>(1);
  const [localEndDate, setLocalEndDate] = useState<DateTime>(endDate);
  const { handleNewInterval } = useContext(AuthenticatedContext);

  const marks = [
    {
      value: 1,
      label: "1M",
    },
    {
      value: 2,
      label: "3M",
    },
    {
      value: 3,
      label: "6M",
    },
    {
      value: 4,
      label: "12M",
    },
  ];

  const handleEndDateChange = (value: DateTime | null) => {
    setLocalEndDate(value ? value : DateTime.now().plus({ month: 1 }));
    dateInputsMap(value, false);
    handleNewInterval();
  };

  const handleSliderChange = (event: Event, newValue: any) => {
    setLookaheadValue(newValue);
    dateInputsMap(newValue, true);
  };

  const dateInputsMap = (
    value: number | DateTime | null,
    isLookaheadChange: boolean
  ) => {
    if (isLookaheadChange) {
      switch (value as number | null) {
        case 2:
          setLocalEndDate(DateTime.now().plus({ month: 3 }));
          break;
        case 3:
          setLocalEndDate(DateTime.now().plus({ month: 6 }));
          break;
        case 4:
          setLocalEndDate(DateTime.now().plus({ month: 12 }));
          break;
        default:
          setLocalEndDate(DateTime.now().plus({ month: 1 }));
          break;
      }
    } else {
      const diff = Math.round(
        (value as DateTime).diff(DateTime.now(), ["month"]).months
      );
      if (diff < 3) {
        setLookaheadValue(1);
      } else if (diff < 6) {
        setLookaheadValue(2);
      } else if (diff < 12) {
        setLookaheadValue(3);
      } else if (diff >= 12) {
        setLookaheadValue(12);
      } else {
        setLookaheadValue(1);
      }
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setEndDate(localEndDate);
    }, 500);

    return () => clearTimeout(timeout);
  }, [localEndDate]);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <div
        style={{
          width: 200,
          paddingLeft: 10,
          paddingRight: 10,
          marginRight: 10,
        }}
      >
        <Slider
          value={lookaheadValue}
          defaultValue={1}
          color="secondary"
          onChange={handleSliderChange}
          valueLabelDisplay="off"
          min={1}
          max={4}
          step={1}
          marks={marks}
          size="small"
        />
      </div>
      <div
        style={{
          width: 150,
        }}
      >
        <DesktopDatePicker
          label="End Date"
          inputFormat="dd/LL/yyyy"
          onChange={handleEndDateChange}
          value={localEndDate}
          minDate={DateTime.now()}
          disableMaskedInput
          maxDate={DateTime.now().plus({ month: 12 })}
          renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
            <TextField
              name="endDate"
              {...params}
              size="small"
              onKeyDown={(e) => {
                e.preventDefault();
              }}
            />
          )}
        />
      </div>
    </LocalizationProvider>
  );
};

export default memo(DateRange);
